import { graphql } from 'react-relay';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ssrBannerManager } from '@confluence/ssr-utilities/entry-points/SSRBannerManager.ts';

import { isBannerContentSingleLineFromJSONString } from './isBannerContentSingleLine.ts';
import { useDismissalState } from './useDismissalState.ts';
import { useIsPageEligibleForAdminAnnouncementBanner } from './useIsPageEligibleForAdminAnnouncementBanner.ts';
import { useMigratorPromptEligible } from './useMigratorPromptEligible.ts';
import { useMigratorPromptDismissalState } from './useMigratorPromptDismissalState.ts';
import type {
	useRegisterAdminAnnouncementBannerQuery$data,
	useRegisterAdminAnnouncementBannerQuery$variables,
} from './__generated__/useRegisterAdminAnnouncementBannerQuery.graphql.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

const adminAnnouncementBannerQuery = graphql`
	query useRegisterAdminAnnouncementBannerQuery {
		adminAnnouncementBanner {
			id
			isDismissible
			content
			appearance
			title
			updatedAt
		}
	}
`;

const BANNER_NAME = 'AdminAnnouncementBanner';
export const BANNER_HEIGHT_SINGLE_LINE = 48;
export const BANNER_HEIGHT_MULTI_LINE = 72;

export function useRegisterAdminAnnouncementBanner(
	bannerState: BannerStateContainer | null = null,
): {
	shouldRender: boolean;
	hasOtherBanners: boolean | '' | undefined;
	onDismiss: () => void;
	data: useRegisterAdminAnnouncementBannerQuery$data | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	shouldRenderMigratorPrompt: boolean;
} {
	const { isLicensed } = useSessionData();
	const isPageEligibleForAdminAnnouncementBanner = useIsPageEligibleForAdminAnnouncementBanner();

	const {
		data,
		loading: activeAnnouncementBannerLoading,
		error: activeAnnouncementBannerError,
	} = useQueryInterop<
		useRegisterAdminAnnouncementBannerQuery$data,
		useRegisterAdminAnnouncementBannerQuery$variables
	>(adminAnnouncementBannerQuery, {
		skip: !isPageEligibleForAdminAnnouncementBanner,
	});

	const hasOtherBanners =
		isPageEligibleForAdminAnnouncementBanner &&
		(bannerState?.state?.banners?.some((banner) => banner.name !== BANNER_NAME) ||
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			(process.env.REACT_SSR && ssrBannerManager.hasOtherBanners(BANNER_NAME)));

	// Migrator prompt eligibility
	const { isMigratorPromptEligible, loading: migratorPromptEligibleLoading } =
		useMigratorPromptEligible({
			skip:
				// No SSR support for migrator prompt
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				Boolean(process.env.REACT_SSR) ||
				// Contextual eligibility check
				!isPageEligibleForAdminAnnouncementBanner ||
				hasOtherBanners ||
				// Existing active AAB Query
				activeAnnouncementBannerLoading ||
				Boolean(data?.adminAnnouncementBanner),
		});

	// Dismissal state
	const migratorPromptDismissalState = useMigratorPromptDismissalState();
	const adminAnnouncementDismissalState = useDismissalState({
		isLicensed,
		id: data?.adminAnnouncementBanner?.id,
		updatedAt: data?.adminAnnouncementBanner?.updatedAt,
		isDismissible: data?.adminAnnouncementBanner?.isDismissible,
	});

	const { isDismissed, onDismiss } = isMigratorPromptEligible
		? migratorPromptDismissalState
		: adminAnnouncementDismissalState;

	const loading = activeAnnouncementBannerLoading || migratorPromptEligibleLoading;

	// Banner rendering conditions
	const bannerHasContent = Boolean(data?.adminAnnouncementBanner) || isMigratorPromptEligible;
	const isBannerReadyToDisplay = !loading && !hasOtherBanners && !isDismissed;

	const shouldRender = bannerHasContent && isBannerReadyToDisplay;
	const shouldRenderMigratorPrompt = isMigratorPromptEligible && isBannerReadyToDisplay;

	if (shouldRender) {
		const bannerHeight = isBannerContentSingleLineFromJSONString(
			data?.adminAnnouncementBanner?.content || '',
		)
			? BANNER_HEIGHT_SINGLE_LINE
			: BANNER_HEIGHT_MULTI_LINE;
		bannerState?.show(BANNER_NAME, bannerHeight);
		addUFOCustomData({
			isAdminAnnouncementBannerDisplayed: true,
		});
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			ssrBannerManager.registerBanner({
				name: BANNER_NAME,
				height: bannerHeight,
			});
		}
	} else {
		bannerState?.hide(BANNER_NAME);
	}

	return {
		shouldRender,
		hasOtherBanners,
		onDismiss,
		data,
		loading,
		error: activeAnnouncementBannerError,
		shouldRenderMigratorPrompt,
	};
}
